import ErrorBoundary from '@/components/ErrorBoundary';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig, StepConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import RouteConstants from '@/features/router/RouteConstants';
import { Button } from '@/lib';
import { useCompanyValues } from '@/providers/CompanyProvider';
import {
  PrepareConnectorFlowStepResponse,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  useGetWorkspaceConnectorsQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuTriangleAlert } from 'react-icons/lu';
import { CHANNEL_CONNECTOR_NAMES } from '../modelli/TemplateFlowEditor';
import { WHATSAPP_FLOW_NAMES } from '@/features/connectors/whatsapp';
import { Channel } from '../../NewCommunication/ChannelStepContainer';
import { useNewCommunicationValues } from '../../NewCommunication/context/useNewCommunicationValues';
import { COMUNI_TABS_ITEM, NO_TOPIC } from '../../comuni-helper';
import { TOPIC_SEPARATOR } from '../../templates/topicPrefix';
import { filterStepFormSchema, filterTemplateEnumsByTopic } from '../../templates/filterTemplateEnumsByTopic';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

export type ChannelSendConfig = {
  connectorFlowId: string;
  connectorInstanceId: string;
  flowParams: FlowFormData;
  flowMappings: FlowFormData;
};

const SEND_FLOW: Record<Channel, { legacyFlowNames: readonly string[]; requiresConnectorMatch: boolean; previewVariant: 'whatsapp' | 'sms' | 'email' }> = {
  // Both WhatsApp connectors ship a flow called "Send", as do sms and email: matched on the
  // connected connector, never on the name alone.
  whatsapp: { legacyFlowNames: WHATSAPP_FLOW_NAMES.send, requiresConnectorMatch: true, previewVariant: 'whatsapp' },
  sms: { legacyFlowNames: ['Send'], requiresConnectorMatch: true, previewVariant: 'sms' },
  email: { legacyFlowNames: ['Mailtrap Send Flow'], requiresConnectorMatch: false, previewVariant: 'email' },
};

const NEW_SEND_FLOW_NAME = 'Send';

const hasMappingsUi = (config: FlowMappingsConfig | null) => !!config?.schema?.properties && Object.keys(config.schema.properties).length > 0;

type ChannelSendFlowConfigProps = {
  channel: Channel;
  audienceId: string;
  onConfigured: (config: ChannelSendConfig) => void;
};

const NoMatchingTemplatesAlert: FC<{ onGoToModelli: () => void }> = ({ onGoToModelli }) => {
  const { t } = useTranslation('comuni');
  const { topic } = useNewCommunicationValues();
  const topicLabel = topic === NO_TOPIC ? t('topic.none') : t(`topics.${topic}`);
  const body =
    topic === NO_TOPIC
      ? t('flows.noMatchingTemplatesBodyNone', { topic: topicLabel })
      : t('flows.noMatchingTemplatesBody', { topic: topicLabel, topicPrefix: `${topic}${TOPIC_SEPARATOR}` });

  return (
    <div role='alert' className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-3'>
      <div className='flex gap-3'>
        <LuTriangleAlert className='mt-0.5 size-5 flex-none text-amber-500' aria-hidden />
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-bold text-amber-900'>{t('flows.noMatchingTemplatesTitle')}</p>
          <p className='mt-1 text-sm text-amber-800'>{body}</p>
          <div className='mt-3'>
            <Button intent='secondary' size='md' onClick={onGoToModelli} text={t('flows.goToModelli')} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ChannelSendFlowConfig: FC<ChannelSendFlowConfigProps> = ({ channel, audienceId, onConfigured }) => {
  const cfg = SEND_FLOW[channel];
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const { t } = useTranslation('comuni');
  const { t: tCampaign } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const { topic } = useNewCommunicationValues();

  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId, lang: currentLanguage },
    { skip: !workspaceId }
  );
  const { data: connectorInstances, isLoading: isInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const { data: connectorFlows, isLoading: isFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId, queryParams: { showUserJourney: true } },
    { skip: !workspaceId }
  );

  const connector = customConnectors?.find((item) => CHANNEL_CONNECTOR_NAMES[channel].includes(item.name) && item.isAuthenticated);
  const legacyFlow = connectorFlows?.find((flow) => cfg.legacyFlowNames.includes(flow.name) && (!cfg.requiresConnectorMatch || flow.connectorId === connector?.id));
  const fallbackFlow = connector ? connectorFlows?.find((flow) => flow.name === NEW_SEND_FLOW_NAME && flow.connectorId === connector.id) : undefined;
  const sendFlow = legacyFlow ?? fallbackFlow;
  const sendFlowId = sendFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === sendFlow?.connectorId)?.id;

  const [prepareStep] = usePrepareConnectorFlowStepMutation();
  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [hasMatchingTemplates, setHasMatchingTemplates] = useState(true);
  const [prepareError, setPrepareError] = useState(false);
  const [flowParams, setFlowParams] = useState<FlowFormData | null>(null);
  const [mappingsConfig, setMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);

  const fetchPrepareData = useCallback(async () => {
    if (!connectorInstanceId || !sendFlowId || !audienceId) return;
    setPrepareError(false);
    try {
      const response = await prepareStep({
        workspaceId,
        flowId: sendFlowId,
        audienceId,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();
      const translated = translateI18nValues(response, currentLanguage);
      const rawParams = (translated?.configuration?.elements?.params ?? {}) as PrepareStepResult;
      const { params, hasMatchingTemplates: matched } = filterTemplateEnumsByTopic(
        rawParams as Record<string, StepConfig>,
        topic
      );
      setHasMatchingTemplates(matched);
      setPrepareResult(params as PrepareStepResult);
    } catch {
      setPrepareError(true);
      setPrepareResult(null);
      setHasMatchingTemplates(true);
    }
  }, [audienceId, connectorInstanceId, sendFlowId, prepareStep, currentLanguage, workspaceId, topic]);

  useEffect(() => {
    void fetchPrepareData();
  }, [fetchPrepareData]);

  // Reset local flow/mapping state when the resolved audience or connector flow changes,
  // so stale params/mappings from a previous selection never leak into a new one.
  useEffect(() => {
    setPrepareResult(null);
    setHasMatchingTemplates(true);
    setFlowParams(null);
    setMappingsConfig(null);
    setMappingData({});
    setIsMappingsFormValid(false);
  }, [audienceId, sendFlowId, connectorInstanceId, topic]);

  const handleMappingDataChange = useCallback((data: FlowFormData) => setMappingData(data), []);
  const handleMappingsValidationChange = useCallback((valid: boolean) => setIsMappingsFormValid(valid), []);

  const transformStepFormSchema = useCallback(
    (formSchema: NonNullable<StepConfig['reactJsonSchemaForm']>) => {
      const { formSchema: filtered, hasMatchingTemplates: matched } = filterStepFormSchema(formSchema, topic);
      setHasMatchingTemplates(matched);
      return filtered;
    },
    [topic]
  );

  const goToModelli = useCallback(() => {
    window.open(`${RouteConstants.comuni}?tab=${COMUNI_TABS_ITEM.TEMPLATES}`, '_blank', 'noopener,noreferrer');
  }, []);

  const finish = (params: FlowFormData, mappings: FlowFormData) => {
    if (!sendFlowId || !connectorInstanceId || !hasMatchingTemplates) return;
    onConfigured({ connectorFlowId: sendFlowId, connectorInstanceId, flowParams: params, flowMappings: mappings });
  };

  const handleFlowComplete = (allParams: FlowFormData, config: FlowMappingsConfig) => {
    if (!hasMatchingTemplates) return;
    setFlowParams(allParams);
    setMappingsConfig(config);
    setMappingData({});
    setIsMappingsFormValid(false);
    if (!hasMappingsUi(config)) {
      finish(allParams, {});
    }
  };

  const isConnectorLoading = isConnectorsLoading || isInstancesLoading || isFlowsLoading;
  const isConnectorReady = !!connectorInstanceId && !!sendFlowId;
  const connectorUnavailable = !isConnectorLoading && !isConnectorReady;
  const isPreparePending = isConnectorReady && !prepareResult && !prepareError;
  const isLoadingAnything = isConnectorLoading || isPreparePending;

  const phase: 'form' | 'mappings' = flowParams && hasMappingsUi(mappingsConfig) ? 'mappings' : 'form';

  return (
    <div className='space-y-4'>
      {connectorUnavailable ? (
        <p className='text-sm text-tertiary-600'>{t('flows.connectorUnavailable')}</p>
      ) : (
        <div className={phase === 'form' ? undefined : 'hidden'}>
          {prepareResult && !hasMatchingTemplates ? <NoMatchingTemplatesAlert onGoToModelli={goToModelli} /> : null}
          <ErrorBoundary>
            {prepareResult ? (
              <MultiStepFlowForm
                id={`comuni-send-flow-${channel}`}
                showTemplatePreview
                templatePreviewVariant={cfg.previewVariant}
                audienceId={audienceId}
                connectorInstanceId={connectorInstanceId!}
                connectorFlowId={sendFlowId!}
                paramsConfig={prepareResult as Record<string, StepConfig>}
                transformStepFormSchema={transformStepFormSchema}
                onComplete={handleFlowComplete}
              />
            ) : (
              <div className='flex flex-col items-start gap-3'>
                <p className='text-sm text-tertiary-600'>
                  {isLoadingAnything ? tCampaign('smsWorkspace.templates.templateLoadingMessage') : tCampaign('smsWorkspace.templates.failedToLoadTemplate')}
                </p>
                {prepareError ? (
                  <Button intent='secondary' onClick={() => void fetchPrepareData()} text={tCampaign('smsWorkspace.templates.tryAgain')} />
                ) : null}
              </div>
            )}
          </ErrorBoundary>
        </div>
      )}

      {flowParams && phase === 'mappings' ? (
        <ErrorBoundary>
          <div className='space-y-4'>
            <h3 className='text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={mappingsConfig!}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <div className='flex justify-end gap-3 pt-2'>
              <Button intent='secondary' onClick={() => setFlowParams(null)} text={String(tCampaign('COMMON:previous' as any))} />
              <Button
                disabled={!isMappingsFormValid || !hasMatchingTemplates}
                onClick={() => finish(flowParams, mappingData)}
                text={String(tCampaign('COMMON:confirm' as any))}
              />
            </div>
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
